/**
 * Where the food values come from, as the About page states it. The Food tab never names a
 * source (ADR-033 §8); this is the one place, and it names only the tables the app ships.
 *
 * The INDB and label wording is the licence text in docs/research/2026-09-25/data-sources.md §5,
 * verbatim: INDB is used with acknowledgement and that sentence is the acknowledgement. Only the
 * lead-in is Kaya's own, and it names the mark the dishes actually carry: the Food tab puts "~"
 * before an INDB dish's kcal (SNIPPETS §39) and never the word INDB, so "Dishes marked INDB" sent
 * the reader looking for a label that is not there.
 */

/** A food row's `src`: INDB, USDA SR Legacy, USDA FNDDS, USDA Foundation Foods, a product label. */
export type SourceCode = "i" | "u" | "f" | "o" | "l";

export interface FoodSource {
  src: SourceCode;
  /** the source's short name */
  label: string;
  /** the paragraph for INDB and label; for the three USDA tables, the release named in the
   *  one USDA paragraph they share */
  text: string;
}

export const FOOD_SOURCES: readonly FoodSource[] = [
  {
    src: "i",
    label: "INDB",
    text:
      "Dishes marked “~”, an estimate from the table’s usual recipe: Vijayakumar A., Dubasi H. R., Awasthi A., Jaacks L. M., “Indian Nutrient " +
      "Databank (INDB)”, Current Developments in Nutrition 8(7):103790 (2024), " +
      "doi:10.1016/j.cdnut.2024.103790, derived from Longvah T. et al., Indian Food Composition " +
      "Tables 2017, ICMR–National Institute of Nutrition, Hyderabad. Used with acknowledgement for " +
      "personal use; not for redistribution.",
  },
  { src: "u", label: "USDA", text: "SR Legacy 2018" },
  { src: "f", label: "USDA", text: "FNDDS 2021–2023" },
  { src: "o", label: "USDA", text: "Foundation Foods" },
  {
    src: "l",
    label: "label",
    text: "Packaged foods marked “label”: the manufacturer’s nutrition label.",
  },
];

const USDA: readonly SourceCode[] = ["u", "f", "o"];

/** One paragraph of the notice and the source codes it covers. */
export interface NoticePart {
  src: readonly SourceCode[];
  text: string;
}

/**
 * The notice for the sources present: INDB, then one USDA paragraph naming each USDA release
 * present, then label. Codes it does not know — "c", a food the user entered — say nothing.
 */
export function sourcesNotice(present: ReadonlySet<string>): NoticePart[] {
  const has = (s: FoodSource) => present.has(s.src);
  const out: NoticePart[] = [];
  const indb = FOOD_SOURCES.find((s) => s.src === "i");
  if (indb && has(indb)) out.push({ src: ["i"], text: indb.text });

  const usda = FOOD_SOURCES.filter((s) => USDA.includes(s.src) && has(s));
  if (usda.length)
    out.push({
      src: usda.map((s) => s.src),
      text:
        "Staples and dishes marked USDA: U.S. Department of Agriculture, Agricultural Research " +
        `Service. FoodData Central (${usda.map((s) => s.text).join("; ")}). fdc.nal.usda.gov. ` +
        "Public domain (CC0 1.0); citation requested, not required.",
    });

  const label = FOOD_SOURCES.find((s) => s.src === "l");
  if (label && has(label)) out.push({ src: ["l"], text: label.text });
  return out;
}

/** What the notice assumes while the food table is still loading, or when it did not load. */
export const DEFAULT_SOURCES: ReadonlySet<SourceCode> = new Set<SourceCode>(["i", "u", "f", "o", "l"]);

/** The one caveat the figures carry, said once, where the sources are. */
export const FOOD_CAVEAT =
  "Home-cooked figures vary by roughly a third with oil and portion alone, so every figure is an " +
  "estimate. Log the same way each day and read the trend, not any single day.";
